import React, { useCallback, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { UncontrolledTooltip, Row, Col, Label } from 'reactstrap';
import formatDateFns from 'src/utils/formatDateFns';
import ReactTooltip from 'react-tooltip';
import useAuth from 'src/hooks/useAuth';
import { useIntl } from 'react-intl';
import { ParseError } from 'src/services/downloadFileUrl';
import http from 'src/services/httpService';
import { getConfiguration } from 'src/services/commonRequests';
import Content from 'src/componentes/pages/content';
import ListColumn from 'src/componentes/NewList/ListColumn';
import Toolbar from 'src/componentes/NewList/Toolbar';
import Results from 'src/componentes/NewList/Results';
import Button from 'src/componentes/button';
import Input from 'src/componentes/input';
import LoadingContainer from 'src/componentes/loading-container';
import Confirm from 'src/componentes/message-box/confirm';
import ExportLimitMessage from 'src/componentes/export/export-limit-message';
import {
  getColumns,
  getFilter,
  getData,
  getPermissions,
  exportList,
} from 'src/componentes/NewList';
import ArquivoUploadAnexos from 'src/componentes/arquivo-upload/anexos';
import ContentManager from 'src/componentes/content-manager';
import LucideIcon from 'src/componentes/lucide-icon';
import Radio from 'src/componentes/radio';
import Checkbox from 'src/componentes/checkbox';
import {
  FILTROS,
  COLUNAS,
  STATUS_PONTO_CONTROLE_REVISAO,
  ENTIDADES_CUSTOMIZADAS,
} from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import errorHandler from 'src/utils/error-handler';
import Hint from 'src/paginas/item/componentes/hint';
import Legenda from 'src/paginas/gestao-risco/legenda-farol-revisao';
import pPipe from 'src/utils/pPipe.js';
import DropdownEfetividade from './components/efetividade-controle-dropdown';

import SearchSchema from './SearchSchema';
import Alert from 'src/componentes/message-box/alert';
import SearchDrawer from './components/SearchDrawer/SearchDrawer';

const RevisaoEdit = React.lazy(() =>
  import(
    'src/paginas/gestao-risco/risco/RevisaoPontoControle/RevisaoPontoControleEditView'
  )
);
const RiscoPontoControleRevisaoEdit = RevisaoEdit;
const PontoControleEdit = React.lazy(() =>
  import('src/paginas/gestao-risco/ponto-controle/edit')
);
const PlanoAcaoModal = React.lazy(() =>
  import('src/paginas/acao/componentes/PlanoAcaoModal')
);
const RiscoEditView = React.lazy(() =>
  import('src/paginas/gestao-risco/risco/RiscoEditView')
);
const CriteriosEfetividadeControle = React.lazy(() =>
  import('./components/CriteriosEfetividadeControle')
);

function ListRevisaoPontoControle({
  entityTag,
  columnsTag,
  filterTag,
  endpoint,
  parameters,
  defaultSort,
  defaultSortBy,
  exibirAuditado,
  selectedFilter,
  openLastFilter,
  showToolbar,
  showTitle,
  simplifyed,
  showNew,
  idRiscoPontoControle,
  utilizarTituloRevisoesPendentes = false,
  dateFilterFormat,
  utilizarClickRenderNomePontoControle = true,
  hideControlesFilter = false,
  showFastSearch = true,
  ...rest
}) {
  const [updatedItems, setUpdatedItems] = useState([]);
  const [data, setData] = useState([]);
  const [, setDataOriginal] = useState([]);
  const [count, setCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [, setPermissions] = useState({});
  const [filter, setFilter] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(defaultSort);
  const [sortFieldBy, setSortFieldBy] = useState(defaultSortBy);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [defaultColumns, setDefaultColumns] = useState([]);
  const [configuration, setConfig] = useState(null);

  const configurationRef = React.useRef(configuration);
  useEffect(() => {
    configurationRef.current = configuration;
  }, [configuration]);

  const intl = useIntl();
  const { terms: resources, configurations, user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);
  const tiposAnexos = configurations.tiposAnexos;
  const styleCustomFields = { minWidth: 150, maxHeight: '100px' };

  const loadColumns = async (defaultColumns) => {
    try {
      const currentColumns = await getColumns(
        defaultColumns,
        entityTag,
        columnsTag,
        null,
        styleCustomFields
      );
      setColumns(currentColumns.columns);
      setAllColumns(currentColumns.allColumns);
    } catch (err) {
      errorHandler(err);
    }
  };

  const loadData = useCallback(
    async (defaultColumns, filter) => {
      try {
        if (!defaultColumns || !defaultColumns.length)
          defaultColumns = _getDefaultColumns(defaultColumns);

        setLoading(true);
        const newPermissions = await getPermissions(endpoint, parameters);
        setPermissions(newPermissions);

        let newData = [];

        if (newPermissions.allowView || newPermissions.allowList) {
          await loadColumns(defaultColumns);

          let newFilter = selectedFilter;
          if (openLastFilter && filterTag)
            newFilter = await getFilter(filterTag, true);

          if (!newFilter) newFilter = { valor: {} };

          if (filter && filter.valor) newFilter.valor = filter.valor;

          setFilter(newFilter);
          let valueFilter = {};
          if (newFilter != null) valueFilter = newFilter.valor;

          const model = { ...valueFilter, searchText };
          newData = await getData(
            endpoint,
            model,
            sortField,
            sortFieldBy,
            pageSize,
            currentPage - 1,
            parameters
          );
          setDataOriginal(JSON.parse(JSON.stringify(newData.list)));
          setData(newData.list);
          setCount(newData.count);
        }
        setLoading(false);
        return newData;
      } catch (err) {
        setLoading(false);
        errorHandler(err);
      }
    },
    [
      endpoint,
      parameters,
      searchText,
      pageSize,
      currentPage,
      sortField,
      sortFieldBy,
    ]
  );

  useEffect(() => {
    pPipe(
      getConfiguration('PontoControleConfiguracao')(setConfig),
      (config) => config,
      _getDefaultColumns,
      (colunas) => {
        setDefaultColumns(colunas);
        return colunas;
      },
      loadData
    )();
  }, [
    loadData,
    pageSize,
    currentPage,
    searchText,
    sortField,
    sortFieldBy,
    selectedFilter,
  ]);

  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
  });

  const handleColumnsChange = useCallback(async () => {
    await loadColumns(defaultColumns);
    await loadData(defaultColumns);
  });

  const handleRefresh = useCallback(
    async (filter) => {
      let cols = defaultColumns;
      const currentConfig = configurationRef.current || configuration;

      if (currentConfig) {
        cols = _getDefaultColumns(currentConfig);
        setDefaultColumns(cols);
      }
      await loadColumns(cols);
      await loadData(cols, filter);
    },
    [defaultColumns, configuration]
  );

  const handleChangeCurrentPage = useCallback((newValue) => {
    setCurrentPage(newValue);
  });

  const handleChangePageSize = useCallback((pageSize) => {
    setPageSize(pageSize);
  });

  const handleSortColumn = useCallback((sort) => {
    setSortField(sort);
  });

  const handleSortByColumn = useCallback((sortBy) => {
    setSortFieldBy(sortBy);
  });

  const handleExport = async (fileExtension) => {
    try {
      let valueFilter = {};
      if (filter != null) valueFilter = filter.valor;

      let newSearchModel = {
        searchText,
        ...valueFilter,
      };

      if (count > 1000) {
        ContentManager.addContent(
          <Confirm
            title={intl.formatMessage({
              id: 'tituloExportacaoComLimite',
            })}
            isFunction
            message={() => <ExportLimitMessage />}
            labelConfirm={intl.formatMessage({
              id: 'exportar',
            })}
            handleConfirm={async () => {
              try {
                setLoading(true);

                await exportList(
                  newSearchModel,
                  endpoint,
                  sortField,
                  currentPage,
                  parameters,
                  fileExtension,
                  1000
                );
              } catch (err) {
                errorHandler(ParseError(err));
              } finally {
                setLoading(false);
              }
            }}
          />
        );
      } else {
        setLoading(true);
        await exportList(
          newSearchModel,
          endpoint,
          sortField,
          currentPage,
          parameters,
          fileExtension
        );
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      errorHandler(ParseError(err));
    }
  };

  const renderRisco = (item) => {
    return (
      <div style={{ maxHeight: '100px' }}>
        <a tabIndex="-1" onClick={() => handleAbrirRiscoClick(item)}>
          {item.nomeRisco}
        </a>
      </div>
    );
  };

  const renderNomePontoControle = (item) => {
    if (!utilizarClickRenderNomePontoControle)
      return (
        <div style={{ maxHeight: '100px' }}>
          <span>{item.descricaoPontoControle}</span>
        </div>
      );

    return (
      <div style={{ maxHeight: '100px' }}>
        <a tabIndex="-1" onClick={() => handleAbrirPontoControleClick(item)}>
          {item.descricaoPontoControle}
        </a>
      </div>
    );
  };

  const renderDataRevisao = (item) => {
    if (!item.dataRevisao) return;

    return (
      <div>
        {format(new Date(item.dataRevisao), currentDisplay, {
          locale: currentLocale,
        })}
      </div>
    );
  };

  const renderAcessarRevisao = (item) => {
    if (!item.dataRevisao) return;

    return (
      <a tabIndex={-1} onClick={() => handleDataClick(item)}>
        {intl.formatMessage({ id: 'acessarRevisao' })}
      </a>
    );
  };

  const renderConformidade = (item, config) => {
    var id = `id${item.id}`;
    var idPlanoAcao = `idPlanoAcao${item.id}`;

    const planoAcaoObrigatorioNaoAssociado =
      config &&
      config.planoAcaoObrigatorioRevisaoNaoConforme &&
      item?.planosAcao &&
      item.planosAcao.length == 0;

    return (
      <Row>
        <Col md={12}>
          <div className="d-flex inline pt-1 pl-2">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ width: '130px' }}>
                <Radio
                  name={id}
                  disabled={
                    item.permissions != null
                      ? !item.permissions?.allowEdit
                      : false
                  }
                  model={{
                    value: item.conformidade,
                    requestChange: (value) =>
                      handleChangeItem(item, 'conformidade', value, true),
                  }}
                  options={item.opcoesConformidade}
                  labelField="descricao"
                  idField="id"
                />
              </div>
              <div
                style={{
                  width: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: -25,
                }}
              >
                {item.conformidade && item.conformidade.id === 2 && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <LucideIcon
                      id={idPlanoAcao}
                      size={20}
                      style={{
                        marginTop: 4,
                        cursor: 'pointer',
                        color: '',
                      }}
                      className="ml-1"
                      icon="chart-gantt"
                      onClick={() => handlePlanoAcaoClick(item)}
                    />
                    {item?.planosAcao && item.planosAcao.length > 0 && (
                      <div className="pl-1">
                        <span
                          style={{
                            backgroundColor: '#bfbfbf',
                            height: 18,
                            padding: '0 5px',
                            display: 'inline-block',
                            fontSize: 12,
                            lineHeight: '18px',
                          }}
                          className="badge"
                          data-tip
                          data-for={`global-${item.id}`}
                        >
                          {item.planosAcao.length}
                        </span>
                        <ReactTooltip
                          id={`global-${item.id}`}
                          aria-haspopup="true"
                        >
                          <p style={{ fontSize: 15 }}>
                            {intl.formatMessage(
                              { id: 'acoesVinculadas' },
                              { acoes: capitalizeFirstLetter(resources.acoes) }
                            )}
                          </p>
                          <ul>{renderToolTipDescricoes(item)}</ul>
                        </ReactTooltip>
                      </div>
                    )}
                    <Label
                      style={{
                        textAlign: 'center',
                        display: 'block',
                        whiteSpace: 'nowrap',
                        paddingTop: '0.4rem',
                      }}
                    >
                      {planoAcaoObrigatorioNaoAssociado ? ' *' : ''}
                    </Label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  function renderToolTipDescricoes(item) {
    return item?.planosAcao?.map((acao, index) => (
      <li key={acao.id ?? acao.codigo ?? index} style={{ fontSize: 15 }}>
        {acao.codigo} - {acao.descricao}
      </li>
    ));
  }

  const renderAuditado = (item) => {
    if (!item.temPermissaoAuditoria) return null;

    var idRevisao = `idRevisao${item.id}`;

    return (
      <div>
        <div id={idRevisao}>
          <Checkbox
            cursor={!item.podeEditarAuditado ? 'not-allowed' : ''}
            model={{
              value: item && item.auditado,
              requestChange: !item.podeEditarAuditado
                ? undefined
                : (value) => handleChangeItem(item, 'auditado', value),
            }}
          />
        </div>
        {item.status.id !== STATUS_PONTO_CONTROLE_REVISAO.CONCLUIDO && (
          <UncontrolledTooltip
            hideArrow={true}
            placement="left"
            target={idRevisao}
            style={{
              position: 'absolute',
              top: -15,
              right: 42,
              width: 145,
            }}
          >
            {intl.formatMessage({ id: 'revisaoStatusConcluido' })}
          </UncontrolledTooltip>
        )}
      </div>
    );
  };

  const renderAnexo = (item) => {
    return (
      <ArquivoUploadAnexos
        formGroupStyle={{ paddingTop: 10, marginBottom: 5 }}
        tipoAnexo={tiposAnexos.riscoPontoControleRevisao}
        onlyRead={
          item?.permissions != null ? !item.permissions?.allowEdit : false
        }
        colorBadge="#bfbfbf"
        colorValue="#FFF"
        anexoObrigatorio={item.anexoObrigatorio}
        model={{
          value: item.arquivos,
          requestChange: (arquivos) =>
            handleChangeAnexo(item, 'arquivos', arquivos),
        }}
      />
    );
  };

  const renderFarol = (item) => {
    return (
      <span title={item.farol?.descricao}>
        {item.farol && item.farol.cor && (
          <>
            <LucideIcon
              icon={item.farol.icone}
              color={item.farol.cor}
              colorHover={item.farol.cor}
              size={22}
            />
          </>
        )}
      </span>
    );
  };

  const renderDescricaoRisco = (item) => {
    return (
      <div
        style={{
          maxHeight: '130px',
          alignSelf: 'flex-start',
        }}
      >
        {item.descricaoRisco}
      </div>
    );
  };

  const renderComentario = (item) => {
    return showTitle ? (
      <Input
        style={{ marginTop: 10 }}
        rows={3}
        type="textarea"
        disabled={
          item.permissions != null ? !item.permissions?.allowEdit : false
        }
        model={{
          value: item.comentario,
          requestChange: (value) => handleChangeItem(item, 'comentario', value),
        }}
      />
    ) : (
      <Row style={{ maxHeight: '100px' }}>
        <Col md={12}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {item.comentario}
          </div>
        </Col>
      </Row>
    );
  };

  const handleCriteriosEfetividade = (item, disabled) => {
    ContentManager.addContent(
      <CriteriosEfetividadeControle
        disabled={disabled}
        item={item}
        revisaoPontoControleId={item.id}
        afterClose={(resultadoEfetividade) => {
          if (resultadoEfetividade && !disabled) {
            handleRefresh();
          }
        }}
      />
    );
  };

  const renderCalcularEfetividadeControle = (item, disabled = false) => {
    return (
      <div style={{ maxHeight: '100px', cursor: 'pointer' }}>
        <a
          tabIndex="-1"
          onClick={() => handleCriteriosEfetividade(item, disabled)}
          title={intl.formatMessage(
            { id: 'labelCalculeEfetividadeRevisaoCriterios' },
            { controle: resources.pontodecontrole }
          )}
        >
          {item.efetividadeCalculada
            ? item.efetividadeCalculada + '%'
            : intl.formatMessage({ id: 'labelCalcularEfetividade' })}
        </a>
      </div>
    );
  };

  const renderEfetividadeControle = (item) => {
    if (item?.efetividadeControlePorFormula)
      return renderCalcularEfetividadeControle(
        item,
        item.status?.id == STATUS_PONTO_CONTROLE_REVISAO.CONCLUIDO
      );

    const filtro = {
      ignorarCorretivo: !item.controleCorretivo,
      ignorarPreventivo: !item.controlePreventivo,
    };

    return (
      <DropdownEfetividade
        resources={resources}
        disabled={
          item.status &&
          item.status.id === STATUS_PONTO_CONTROLE_REVISAO.CONCLUIDO
        }
        showErrorMessage={!item.funcaoControlePreenchida}
        filtro={item.funcaoControlePreenchida ? filtro : null}
        model={{
          value: item.efetividadeControle,
          onChange: (value) =>
            handleChangeItem(item, 'efetividadeControle', value),
        }}
      />
    );
  };

  const renderImpactosIndiretos = (item) => {
    return (
      <div className="pt-3" style={{ maxHeight: '100px' }}>
        {item.impactosIndiretos &&
          item.impactosIndiretos.map((itemImpacto, i) => (
            <div key={itemImpacto.id ?? itemImpacto.nome ?? i}>
              <Hint item={item} itemImpacto={itemImpacto} index={i} />
            </div>
          ))}
      </div>
    );
  };

  const setNewUpdatedItem = (item, field, valor) => {
    if (!item) return;

    setUpdatedItems((prev) => {
      let newArray = [...prev];
      let currentIndex = newArray.findIndex((i) => i.id === item.id);
      if (currentIndex < 0) {
        newArray.push(item);
      } else {
        if (
          field != null &&
          field === 'concluirRevisao' &&
          !valor &&
          !item.editado
        )
          //remover item, caso usuário tenha clicado no checkbox duas vezes
          newArray = newArray.filter((i) => i.id !== item.id);
        else newArray[currentIndex] = item;
      }
      return newArray;
    });
  };

  async function handleChangeItem(item, field, valor, radioButton) {
    try {
      if (radioButton)
        item[field] =
          item[field] && valor && item[field].id === valor.id
            ? { id: -1, descricao: intl.formatMessage({ id: 'naoInformado' }) }
            : valor;
      else item[field] = valor;
      item.editado = true;
      setNewUpdatedItem(item, field, valor);
    } catch (err) {
      errorHandler(err);
    }
  }

  async function handleChangeAnexo(item, field, arquivos) {
    try {
      await http.post(`${endpoint}/SalvarArquivos`, {
        id: item.id,
        arquivos: arquivos,
      });
      //await loadData(defaultColumns); retirado pelo atendimento GDR-594
      setUpdatedItems((prev) => {
        let newArray = [...prev];
        item[field] = arquivos;
        newArray.push(item);
        return newArray;
      });
    } catch (err) {
      errorHandler(err);
    }
  }

  function handleDataClick(item) {
    try {
      ContentManager.addContent(
        <RevisaoEdit
          handleCallBack={() => loadData(defaultColumns)}
          item={item}
          parameters={{
            id: item.id,
            idRiscoPontoControle: item.idRiscoPontoControle,
          }}
        ></RevisaoEdit>
      );
    } catch (err) {
      return '';
    }
  }

  function handleAbrirPontoControleClick(item) {
    ContentManager.addContent(
      <PontoControleEdit
        item={item.pontoControle}
        parameters={{ id: item.pontoControle.id }}
        afterSave={() => loadData(defaultColumns)}
      ></PontoControleEdit>
    );
  }

  function handleAbrirRiscoClick(item) {
    if (!item) return;

    ContentManager.addContent(
      <RiscoEditView
        mode="edit"
        handleClose={handleRefresh}
        item={item.risco}
        {...rest}
      />
    );
  }

  function handlePlanoAcaoClick(item) {
    ContentManager.addContent(
      <PlanoAcaoModal
        model={item}
        showMenuAcao={false}
        refresh={() => handleRefreshPlanoAcao(item)}
      />
    );
  }

  async function handleRefreshPlanoAcao(item) {
    try {
      const novasAcoes = await getPlanosAcaoByItemId(item);

      //"data" atualiza no List da tela
      setData((prevData) =>
        prevData.map((d) =>
          d.id === item.id ? { ...d, planosAcao: novasAcoes } : d
        )
      );

      //"updateItens" é o Model que está indo atualizado para o Back-end
      setUpdatedItems((prevData) =>
        prevData.map((d) =>
          d.id === item.id ? { ...d, planosAcao: novasAcoes } : d
        )
      );
    } catch (err) {
      console.error(err);
    }
  }

  async function getPlanosAcaoByItemId(item) {
    try {
      const response = await http.post(`Acao/List`, {
        model: {
          item: item.item,
        },
        pageSize: 40,
        page: 0,
        sort: 'UltimaVersao.Descricao',
        parameters: {
          item: item.item,
          riscoPontoControleRevisaoId: item.id,
          itemId: item.item.id,
        },
      });
      return response.data?.list || [];
    } catch (err) {
      errorHandler(err);
      return [];
    }
  }

  const handleSaveClick = async () => {
    setLoading(true);

    try {
      setUpdatedItems([]);

      await http.post(`${endpoint}/SalvarRevisoesPendentes`, {
        model: updatedItems,
      });

      ContentManager.addContent(
        <Alert message={intl.formatMessage({ id: 'revisaoSalvaSucesso' })} />
      );

      await loadData(defaultColumns);
    } catch (err) {
      errorHandler(err);
    } finally {
      setLoading(false);
    }
    setUpdatedItems([]);
  };

  const renderConcluirRevisao = (item) => {
    return (
      <Checkbox
        disabled={
          item.status &&
          (item.status.id === STATUS_PONTO_CONTROLE_REVISAO.CONCLUIDO ||
            item.status.id === STATUS_PONTO_CONTROLE_REVISAO.EM_VALIDACAO)
        }
        model={{
          value: item && item.concluirRevisao,
          requestChange: (value) =>
            handleChangeItem(item, 'concluirRevisao', value),
        }}
      />
    );
  };

  function _getDefaultColumns(config) {
    const colunas = [
      <ListColumn
        visible={true}
        minWidth={150}
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'acessarRevisao' },
            { revisaodecontrole: resources.revisaodecontrole }
          )
        )}
        valueFunction={renderAcessarRevisao}
        key="acessarRevisaoControle"
        valueField="acessarRevisaoControle"
        default
        required
      />,
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'idIntegracao' })
        )}
        minWidth={130}
        default
        key="idIntegracao"
        valueField="idIntegracao"
        sortField="UltimaVersao.IdIntegracao"
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(resources.risco)}
        key="risco"
        valueField="risco"
        valueFunction={renderRisco}
        minWidth={150}
        default
        sortField="RiscoPontoControle.Risco.UltimaVersao.Nome"
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'label.itensDeImpactoIndireto' })
        )}
        key="impactosIndiretos"
        valueField="impactosIndiretos"
        minWidth={170}
        valueFunction={renderImpactosIndiretos}
        default
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'labelCodigoImportacaoPontoControle' },
            { pontocontrole: resources.pontodecontrole }
          )
        )}
        minWidth={200}
        key="idIntegracaoPontoControle"
        valueField="idIntegracaoPontoControle"
        default
        sortField="RiscoPontoControle.PontoControle.IdIntegracao"
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'descricao' })
        )}
        key="descricaoPontoControle"
        valueField="descricaoPontoControle"
        valueFunction={renderNomePontoControle}
        minWidth={200}
        default
        sortField="RiscoPontoControle.PontoControle.Descricao"
      />,
      <ListColumn
        visible={true}
        minWidth={130}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'label.dataRevisao' })
        )}
        key="dataRevisao"
        valueField="dataRevisao"
        valueFunction={renderDataRevisao}
        sortField="UltimaVersao.DataRevisao"
        required
      />,
      <ListColumn
        visible={true}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'label.responsavel' })
        )}
        key="responsavelRevisao"
        valueField="responsavelRevisao"
        default
        sortField="UltimaVersao.ResponsavelRevisao.Nome"
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'periodicidade' })
        )}
        key="periodicidadePontoControle"
        valueField="periodicidadePontoControle"
        default
        sortField="RiscoPontoControle.PontoControle.Periodicidade"
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'tipoPontoControle' },
            { pontodecontrole: resources.pontodecontrole }
          )
        )}
        key="tipoPontoControle"
        valueField="tipoPontoControle"
        sortField="RiscoPontoControle.PontoControle.TipoPontoControle"
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'tolerancia' })
        )}
        key="diasToleranciaRevisao"
        valueField="diasToleranciaRevisao"
        sortField="RiscoPontoControle.PontoControle.DiasToleranciaRevisao"
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'tipoTolerancia' })
        )}
        key="tipoTolerancia"
        valueField="tipoTolerancia"
        sortField="RiscoPontoControle.PontoControle.TipoTolerancia"
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'validacoesPendentes' })
        )}
        key="validadoresPendentes"
        valueField="validadoresPendentes"
        minWidth={170}
        default
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'validadores' })
        )}
        key="validadores"
        valueField="validadores"
        default
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'observacao' })
        )}
        key="observacaoPontoControle"
        valueField="observacaoPontoControle"
        maxHeight={100}
        minWidth={250}
      />,
      <ListColumn
        visible={true}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'label.comentario' })
        )}
        key="comentario"
        valueField="comentario"
        valueFunction={renderComentario}
        required={config?.tornarObrigatorioCampoComentarioRevisaoControle}
        default
        sortField="UltimaVersao.Descricao"
      />,
      <ListColumn
        visible={!simplifyed && config?.avaliarConformidadeRevisaoControle}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'conformidade' })
        )}
        key="conformidade"
        valueField="conformidade"
        valueFunction={(item) => renderConformidade(item, config)}
        sortField="UltimaVersao.Conformidade"
        required={
          config?.tornarObrigatoriaAvaliarConformidadeConclusaoRevisaoControle
        }
      />,
      <ListColumn
        visible={true}
        headerText={capitalizeFirstLetter(resources.farol)}
        key="farol"
        valueField="farol"
        valueFunction={renderFarol}
        default
        sortField="UltimaVersao.Farol"
      />,
      <ListColumn
        visible={true}
        headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'status' }))}
        key="statusRevisao"
        valueField="statusRevisao"
        minWidth={100}
        default
      />,
      <ListColumn
        visible={!simplifyed}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'tipoEvidencias' })
        )}
        key="tipoEvidencias"
        valueField="tipoEvidencias"
      />,
      <ListColumn
        visible={true}
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'descricaoRisco' },
            { risco: resources.risco }
          )
        )}
        key="descricaoRisco"
        valueField="descricaoRisco"
        default
        sortField="RiscoPontoControle.Risco.UltimaVersao.Descricao"
        valueFunction={(item) => renderDescricaoRisco(item)}
        minWidth={200}
      />,
      <ListColumn
        visible={
          !simplifyed && config.permitirAvaliarEfetividadeRevisaoControle
        }
        minWidth={220}
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'efetividadeControle' },
            { pontodecontrole: resources.pontodecontrole }
          )
        )}
        key="efetividadeControle"
        valueField="efetividadeControle"
        valueFunction={(item) => renderEfetividadeControle(item)}
      />,
      <ListColumn
        visible={true}
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'areasDoRisco' },
            { areas: resources.areas, risco: resources.risco }
          )
        )}
        key="areaRisco"
        valueField="areaRisco"
        sortField="RiscoPontoControle.Risco.UltimaVersao.Area.Nome"
      />,
      <ListColumn
        visible={true}
        headerText={capitalizeFirstLetter(
          intl.formatMessage(
            { id: 'areasDoControle' },
            {
              areas: resources.areas,
              pontodecontrole: resources.pontodecontrole,
            }
          )
        )}
        key="areaControle"
        valueField="areaControle"
        sortField="RiscoPontoControle.PontoControle.Area.Nome"
      />,
      <ListColumn
        visible={true}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'concluirRevisao' })
        )}
        valueFunction={renderConcluirRevisao}
        key="concluirRevisao"
        valueField="concluirRevisao"
        required
      />,
    ];

    if (config && config.utilizarAuditoria)
      colunas.push(
        <ListColumn
          visible={exibirAuditado || !simplifyed}
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'auditado' })
          )}
          key="auditado"
          valueField="auditado"
          default
          valueFunction={renderAuditado}
          sortField="UltimaVersao.Auditado"
        />
      );

    if (config && config.anexoObrigatorio)
      colunas.push(
        <ListColumn
          visible
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'label.anexo' })
          )}
          key="anexos"
          valueField="anexos"
          valueFunction={renderAnexo}
          default
        />
      );

    if (config && config.funcaoPontoControle)
      colunas.push(
        <ListColumn
          visible={config.funcaoPontoControle}
          headerText={capitalizeFirstLetter(
            intl.formatMessage(
              { id: 'funcaoPontoControle' },
              { pontodecontrole: resources.pontodecontrole }
            )
          )}
          key="funcaoPontoControle"
          valueField="funcaoPontoControle"
          sortField="FuncaoPontoControle"
        />
      );

    if (config && config.tipoDeControle)
      colunas.push(
        <ListColumn
          visible={config.tipoDeControle}
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'tipoDeControle' })
          )}
          key="tipoDeControle"
          valueField="tipoDeControle"
          sortField="TipoDeControle"
        />
      );
    return colunas;
  }

  const handleNewClick = () => {
    ContentManager.addContent(
      <RiscoPontoControleRevisaoEdit
        mode="edit"
        handleClose={handleRefresh}
        parameters={{
          ...parameters,
          idRiscoPontoControle: idRiscoPontoControle,
        }}
      />
    );
  };

  const actions = [
    <Button key={1} className="ml-2" onClick={handleSaveClick} type="primary">
      <LucideIcon icon="content-save-outline" />
      {updatedItems.length > 0 && (
        <span className="ml-2">{updatedItems.length}</span>
      )}
    </Button>,
  ];

  const content = (
    <LoadingContainer isLoading={isLoading}>
      {showToolbar && (
        <Toolbar
          useSavedFilter
          showSearch
          showFastSearch={showFastSearch}
          showExport
          onExport={handleExport}
          onColumnsChange={handleColumnsChange}
          onSearchChange={handleSearchChange}
          onRefresh={handleRefresh}
          searchText={searchText}
          searchSchema={SearchSchema}
          filterDrawer={true}
          searchComponent={
            <SearchDrawer
              config={configuration}
              hideControlesFilter={hideControlesFilter}
            />
          }
          filter={filter}
          filterTag={filterTag}
          columnsTag={columnsTag}
          columns={columns}
          allColumns={allColumns}
          actions={actions}
          tipoItem={ENTIDADES_CUSTOMIZADAS.PONTO_CONTROLE_REVISAO}
          ignorarCamposCustomizados={false}
          showNew={showNew}
          handleNewClick={handleNewClick}
          permissions={{ allowCreate: true }}
          dateFilterFormat={dateFilterFormat}
          isLoading={isLoading}
        />
      )}
      <Results
        showCustomFields={true}
        resources={resources}
        count={count}
        columns={columns}
        data={data}
        currentPage={currentPage}
        pageSize={pageSize}
        onChangeCurrentPage={handleChangeCurrentPage}
        onChangePageSize={handleChangePageSize}
        onSortColumn={handleSortColumn}
        onSortByColumn={handleSortByColumn}
        sort={sortField}
        sortBy={sortFieldBy}
        updatedItems={updatedItems}
        url={'/RiscoPontoControleRevisao'}
        handleEditClick={handleDataClick}
        onRefresh={handleRefresh}
        idLabelEditar={'acessarRevisao'}
        idLabelExcluir={'excluirRevisao'}
        showMenu
        sticky={true}
      />
      <Legenda tipoFarol={2} />
    </LoadingContainer>
  );

  return (
    <React.Fragment>
      {showTitle && !utilizarTituloRevisoesPendentes ? (
        <Content
          titulo={intl.formatMessage(
            { id: 'revisoesDePontoControle' },
            { revisoesdecontrole: resources.revisoesdecontrole }
          )}
        >
          {content}
        </Content>
      ) : showTitle && utilizarTituloRevisoesPendentes ? (
        <Content titulo={intl.formatMessage({ id: 'revisoesPendentes' })}>
          {content}
        </Content>
      ) : (
        <Content>{content}</Content>
      )}
    </React.Fragment>
  );
}

ListRevisaoPontoControle.defaultProps = {
  filterTag: FILTROS.REVISOES_PONTO_CONTROLE,
  columnsTag: COLUNAS.REVISOES_PONTO_CONTROLE,
  endpoint: '/RiscoPontoControleRevisao',
  entityTag: ENTIDADES_CUSTOMIZADAS.PONTO_CONTROLE_REVISAO,
  defaultSort: 'UltimaVersao.DataRevisao',
  defaultSortBy: 'asc',
  showToolbar: true,
  showTitle: true,
};
export default ListRevisaoPontoControle;
